import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'cta' | 'secondary' | 'ghost' | 'destructive'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  asChild?: boolean
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'

const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-kasa-green-600 text-white hover:bg-kasa-green-700 focus-visible:ring-kasa-green-600',
  cta:
    'bg-kasa-amber text-white hover:bg-kasa-amber-dark focus-visible:ring-kasa-amber',
  secondary:
    'border border-kasa-green-600 text-kasa-green-600 bg-transparent hover:bg-kasa-green-50 focus-visible:ring-kasa-green-600',
  ghost:
    'text-kasa-green-600 bg-transparent hover:bg-kasa-green-50 focus-visible:ring-kasa-green-600',
  destructive:
    'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600',
}

const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      asChild = false,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        disabled={disabled || loading}
        className={cn(base, variants[variant], sizes[size], className)}
        {...props}
      >
        {asChild ? children : (
          <>
            {loading && <Spinner size="sm" className="border-current border-t-transparent" />}
            {children}
          </>
        )}
      </Comp>
    )
  },
)

Button.displayName = 'Button'
